import { Workbox } from 'workbox-window';

import { logger } from '../logger.js';

/**
 * Registers the service worker generated by `vite-plugin-pwa`. Events:
 *   - `installed`      → first install or updated precache fully fetched
 *   - `waiting`        → a new SW is waiting to activate on next navigation
 *   - `controlling`    → the new SW has taken control of the page
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    logger.debug(
      'Service worker unsupported in this environment; skipping register',
    );
    return;
  }

  if (import.meta.env.DEV) {
    // vite-plugin-pwa emits a dev SW in some configurations; skip to avoid
    // caching the Vite HMR boot script. Also unregister any SW that survived
    // from a previous prod build on the same origin (e.g. ngrok tunnel reused
    // for dev) — otherwise it keeps serving stale assets and HMR appears dead.
    logger.debug('Skipping service worker registration in dev mode');
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const reg of regs) {
        void reg.unregister().then((ok) => {
          if (ok) logger.info('Unregistered stale service worker (dev)');
        });
      }
    });
    if ('caches' in window) {
      void caches.keys().then((keys) => {
        for (const key of keys) void caches.delete(key);
      });
    }
    return;
  }

  const wb = new Workbox('/sw.js');

  wb.addEventListener('installed', (event) => {
    logger.info(`Service worker installed (update=${event.isUpdate ?? false})`);
  });
  wb.addEventListener('waiting', () => {
    logger.info('Service worker waiting to activate');
  });
  wb.addEventListener('controlling', () => {
    logger.info('Service worker is now controlling the page');
  });
  wb.addEventListener('activated', (event) => {
    logger.info(`Service worker activated (update=${event.isUpdate ?? false})`);
  });

  void wb.register().catch((err: unknown) => {
    logger.warn('Service worker registration failed', err);
  });
}
